// @flow
import * as React from 'react';
import InlineHelp from './inlineHelp';
import Button from '@mui/material/Button';
import { 
  FormControl, 
  FormControlLabel, 
  FormHelperText, 
  Switch, 
  InputBase, 
  InputLabel, 
  InputAdornment, 
  Select, 
  MenuItem, 
  Grid, 
  Collapse, 
  Tooltip 
} from '@mui/material';
import Policy from '../classes/policy';
import { IChevronDown } from '~/_base/components/helixIcons';

const remapChild = function remapChild(child, parentProps) {
  switch ((child || {}).type) {
    case MaterialFormGridCollapse:
    case MaterialFormGridControl:
      let inheritProps = {
        source: parentProps.source,
        policy: parentProps.policy,
        onChange: parentProps.onChange,
      };
      return React.cloneElement(child, inheritProps);
    default:
      return child;
  }
};

type MaterialFormGridControlProps = {
  role?: string,
  size?: number,
  label?: string | React.Node,
  prop?: string,
  options?: any,
  source?: any,
  policy?: any,
  onChange?: (prop: string, value: any) => void,
  action?: () => void,
  actionLabel?: string,
  switchComponent?: any,
  GridProps?: any,
  children?: any,
  helpKey?: string,
  disabled?: boolean,
  disabledTooltip?: string,
  tooltip?: string,
  shouldValidate?: boolean,
};
export class MaterialFormGridControl extends React.Component<MaterialFormGridControlProps> {
  render() {
    let { size, source, GridProps, children } = this.props;
    if (source || children) {
      return (
        <Grid {...GridProps} size={size}>
          {this.renderControl()}
        </Grid>
      );
    }
    return null;
  }

  renderControl() {
    let children = this.props.children;
    if (children) {
      let renderChildren = React.Children.map(children, child =>
        remapChild(child, this.props)
      );
      return renderChildren;
    }

    let {
      size,
      label,
      prop,
      options,
      source,
      policy,
      onChange,
      action,
      actionLabel,
      switchComponent,
      GridProps,
      helpKey,
      disabled,
      disabledTooltip,
      tooltip,
      shouldValidate,
      ...otherProps
    } = this.props;
    if (prop == null || source == null || onChange == null) {
      return <span>Requires [prop] when no children</span>;
    }
    let value = source[prop];

    let attrPolicy =
      source[`__${prop}_policy`] ||
      ((policy || {}).attributes || {})[prop] ||
      {};

    let error = source[`__${prop}_policy_error`] || '';
    if (error.length == 0 && policy != null && shouldValidate !== false) {
      error = Policy.validateAttribute(source, prop, policy).join(', ') || '';
    }
    let id = `${prop}-control`;
    let helpComponent = helpKey ? (
      <InlineHelp helpkey={helpKey} rootStyle={{ fontSize: '100%' }} />
    ) : null;
    let labelComponent = (
      <InputLabel
        variant="standard"
        shrink={false}
        htmlFor={id}
        required={attrPolicy.required === true}
      >
        {label}
        {helpComponent}
      </InputLabel>
    );

    const tooltipText = disabled && disabledTooltip 
      ? disabledTooltip 
      : (tooltip ? tooltip : '')

    let writable = attrPolicy.writable !== false;

    if (Array.isArray(options) && options.length > 0) {
      let menuOptions = options;
      if (!React.isValidElement(options[0])) {
        let optionsItems = ((options: any): Array<{
          key: string,
          value: string,
        }>);
        menuOptions = optionsItems.map(option => (
          <MenuItem key={option.key} value={option.key}>
            {option.value}
          </MenuItem>
        ));
      } else {
        menuOptions = options.filter(node => React.isValidElement(node));
      }
      return (
        <FormControl variant="standard" style={{ width: '100%' }}>
          {labelComponent}
          <Tooltip title={tooltipText}>
            <Select
              variant="standard"
              disabled={disabled}
              value={value || ''}
              onChange={e => onChange(prop, e.target.value)}
              input={<InputBase id={id} />}
              MenuProps={{
                TransitionProps: {
                  onEntered: e => this.menuOnEntered(e)
                }
              }}
              IconComponent={IChevronDown}
            >
              {menuOptions}
            </Select>
          </Tooltip>
        </FormControl>
      );
    }

    if (typeof value === 'boolean') {
      let localSwitchComponent = React.cloneElement(
        switchComponent || <Switch color="primary" />,
        {
          checked: value,
          disabled: !writable || disabled,
          onChange: (e) =>
            onChange(prop, e.target.checked),
        }
      );
      return (
        <Tooltip title={tooltipText}>
          <FormControlLabel
            label={
              <span>
                {label}
                {helpComponent}
              </span>
            }
            control={localSwitchComponent}
          />
        </Tooltip>
      );
    }

    let formControlProps = {};
    let formControlHelper = null;
    if (writable === true && error) {
      formControlProps.error = true;
      formControlHelper = (
        <FormHelperText>{error.replace(/{attribute}/g, label)}</FormHelperText>
      );
    }
    let inputProps = { disabled: false };
    if (writable === false || disabled === true) {
      inputProps.disabled = true;
      if (attrPolicy.external === true && policy && policy.external_system) {
        formControlHelper = (
          <FormHelperText>managed in {policy.external_system}</FormHelperText>
        );
      }
    }

    let endAdornment =
      action == null ? null : (
        <InputAdornment position="end">
          <Button style={{ textTransform: 'none' }} onClick={() => action()}>
            {actionLabel || 'Add'}
          </Button>
        </InputAdornment>
      );
    return (
      <FormControl variant="standard" fullWidth {...formControlProps}>
        {labelComponent}
        <Tooltip title={tooltipText}>
          <InputBase
            id={id}
            value={value || ''}
            onChange={e => onChange(prop, e.target.value)}
            endAdornment={endAdornment}
            inputProps={this.props.role ? { role: this.props.role } : {}}
            {...inputProps}
            {...otherProps}
          />
        </Tooltip>

        {formControlHelper}
      </FormControl>
    );
  }

  menuOnEntered(e: any) {
    e.tabindex = -1;
    setTimeout(() => e.focus(), 10);
  }
}

type MaterialFormGridCollapseProps = {
  children?: any,
  source?: any,
  policy?: any,
  onChange?: (prop: string, value: any) => void,
};
export class MaterialFormGridCollapse extends React.Component<MaterialFormGridCollapseProps> {
  render() {
    let { children, ...otherProps } = this.props;
    let renderChildren = React.Children.map(children, child =>
      remapChild(child, this.props)
    );
    return (
      <Grid sx={{ padding: 0 }} size={12}>
        <Collapse in={false} {...otherProps}>
          <Grid container spacing={3} sx={{ padding: '12px' }}>
            {renderChildren}
          </Grid>
        </Collapse>
      </Grid>
    );
  }
}

type MaterialFormGridProps = {
  width?: number,
  source: any,
  policy?: any,
  onChange: (prop: string, value: any) => void,
  children?: any,
};
export default class MaterialFormGrid extends React.Component<MaterialFormGridProps> {
  render() {
    let { width, source, children, onChange, ...otherProps } = this.props;
    if (source) {
      let renderChildren = React.Children.map(children, child =>
        remapChild(child, this.props)
      );

      return (
        <Grid
          container
          spacing={3}
          {...otherProps}
          sx={{ width: width || '100%', margin: '0' }}
        >
          {renderChildren}
        </Grid>
      );
    }
    return null;
  }
}
